import { waitForAllReturnWhenError } from '@confluence/query-preloader-tools/entry-points/waitForAllReturnWhenError.ts';
import { query } from '@confluence/query-preloader-tools/entry-points/graphql.ts';
import { expVal, UNSAFE_expValEqualsNoExposure } from '@confluence/feature-experiments';
import { CONTENT_STATUS_ARCHIVED } from '@confluence/archive-pages/entry-points/archivingConstants.ts';
import { preloadArchivedPageBannerV2Queries } from '@confluence/archive-pages/entry-points/preloadArchivedPageBannerV2Queries.ts';
import {
	PageTitleQuery,
	type PageTitleQuery$data,
	type PageTitleQuery$variables,
} from '@confluence/content-title/entry-points/PageTitleQuery.graphql.ts';

import FolderOverviewChildrenContentQueryNode, {
	type FolderOverviewChildrenContentQuery$data,
	type FolderOverviewChildrenContentQuery$variables,
} from './__generated__/FolderOverviewChildrenContentQuery.graphql.ts';
import DualStreamQueriesFirstPageQueryNode, {
	type DualStreamQueriesFirstPageQuery$data,
	type DualStreamQueriesFirstPageQuery$variables,
} from './__generated__/DualStreamQueriesFirstPageQuery.graphql.ts';
import { FETCH_SIZE, getInitialVisibleChildIds } from './dualStreamVisibleNodes.ts';

type PreloadFolderDataOptions = {
	/** Cloud ID — required by the dual-stream content query. */
	cloudId?: string;
	/** Space key — required by the dual-stream content query. */
	spaceKey?: string;
};

/** The `{ data, errors, hasErrors }` wrapper that `query()` resolves to. */
type QueryResult<TData> = {
	data: TData | null;
	hasErrors: boolean;
};

/**
 * Warms `FolderOverviewChildrenContentQuery` (child history + collaborators) once
 * the overview result is known.
 *
 * This is a chained preload: the enrichment query's `ids` depend on the child ids
 * returned by the first-page query. It runs last so it does not block the
 * independent preloads. The `ids` must match the component's first-render request
 * exactly (order included) or the SSR-warmed store snapshot is treated as
 * incomplete and the columns render blank — see `FolderOverview.tsx`.
 */
async function preloadChildrenContent<TData>(
	overviewTask: Promise<QueryResult<TData>>,
	extractChildIds: (data: TData) => string[],
): Promise<void | QueryResult<FolderOverviewChildrenContentQuery$data>> {
	const { data, hasErrors } = await overviewTask;
	if (hasErrors || !data) {
		return;
	}
	const childIds = extractChildIds(data);
	if (childIds.length === 0) {
		return;
	}
	return query<
		FolderOverviewChildrenContentQuery$data,
		FolderOverviewChildrenContentQuery$variables
	>({
		query: FolderOverviewChildrenContentQueryNode,
		variables: { ids: childIds },
	});
}

/**
 * Warms the archived banner's queries once the overview result shows the folder is archived.
 *
 * The overview only returns `singleContent.status` when `confluence_archive_folder` is on, so this
 * is a no-op for active folders and when the experiment is off. An archived folder's children
 * stream errors by design, so unlike the children preload this reads `data` even when
 * `hasErrors` is set.
 */
async function preloadArchivedBanner<TData>(
	overviewTask: Promise<QueryResult<TData>>,
	contentId: string,
	extractStatus: (data: TData) => string | null | undefined,
): Promise<void> {
	const { data } = await overviewTask;
	if (!data || extractStatus(data) !== CONTENT_STATUS_ARCHIVED) {
		return;
	}
	await preloadArchivedPageBannerV2Queries(contentId);
}

export async function preloadFolderData(
	contentId: string,
	{ cloudId, spaceKey }: PreloadFolderDataOptions = {},
): Promise<void | Error[]> {
	// Preload the folder header title. `FolderHeader` → `FolderInlineTitle`
	// consumes it via `usePageTitle`, which only fetches on the client; without
	// this the folder title renders empty during SSR.
	const titleQueryTask = query<PageTitleQuery$data, PageTitleQuery$variables>({
		query: PageTitleQuery,
		variables: { contentId },
	});

	const overviewQueryTask = query<
		DualStreamQueriesFirstPageQuery$data,
		DualStreamQueriesFirstPageQuery$variables
	>({
		query: DualStreamQueriesFirstPageQueryNode,
		// These variables MUST match the ones passed by `useDualStreamPagination`
		// (empty-string fallbacks for cloudId/spaceKey included) so the store
		// snapshot is complete for the component's operation on SSR.
		variables: {
			cloudId: cloudId ?? '',
			parentId: contentId,
			spaceKey: spaceKey ?? '',
			contentId,
			first: FETCH_SIZE,
			includeDevDocsSyncStatus: expVal('confluence_dev_docs', 'isEnabled', false),
			includeFolderSummary: expVal('confluence_folder_summary', 'isEnabled', false),
			includeArchiveFolderSupport: expVal('confluence_archive_folder', 'isEnabled', false),
			// The byline render owns exposure; preloading must use the same non-exposing decision.
			// eslint-disable-next-line confluence-feature-gating/no-exposure-recommendation -- exposure is recorded when the byline can render
			includeJiraPlannerConnection: UNSAFE_expValEqualsNoExposure(
				'ai_planner_icon_with_confluence_folder',
				'isEnabled',
				true,
			),
		},
	});
	// Chain the children-content and archived-banner preloads off the overview result.
	const childrenQueryTask = preloadChildrenContent(overviewQueryTask, (data) =>
		getInitialVisibleChildIds(data.folders, data.content),
	);
	const bannerQueryTask = preloadArchivedBanner(
		overviewQueryTask,
		contentId,
		(data) => data.singleContent?.status,
	);

	// `waitForAllReturnWhenError` submits any task errors for observability and
	// resolves to `Error[]` on failure or `undefined` on success — its
	// `Awaited<T>[]` return arm is never produced at runtime, so narrow to the
	// real success/error contract.
	const errors = await waitForAllReturnWhenError([
		overviewQueryTask,
		titleQueryTask,
		childrenQueryTask,
		bannerQueryTask,
	]);
	return Array.isArray(errors) ? (errors as Error[]) : undefined;
}
